import * as util from "./util.js";
import * as vectormath from "./vectormath.js";
import * as kdtree from "./kdtree.js";
import * as cconst from "./const.js";
import * as iface from "./interface.js";
import type { AppState } from "./app.js";
import type { UI } from "./ui.js";
import * as sph_presets from "./sph_presets.js";
const config = {
  DIMEN: 64,
  USE_MASK: false,
  PROG_BASE_LAYER: true,
  EXPONENT: 0.1,
  PREPOWER: 3.3291500000000003,
  TONE_IN_SOLVER: false,
  KEEP_UNIFORM: false,
  DRAW_COLORS: true,
  SEARCHRAD: 4.4123,
  RMUL: 4.632090000000001,
  SPH_SPEED: 10.091700000000001,
  PARAM: 1.03318,
  PARAM2: 2.06637,
  PARAM3: 4.5919300000000005,
  PARAM4: 1,
  PARAM5: 4.8215200000000005,
  START_THRESHOLD: 0.44771000000000005,
  GENSTART: 0.15714,
  SHOW_RADII: false,
  DV_DAMPING: 1,
  PROGRESSIVE: true,
  ALTERNATE: false,
  GUASS_MIN: 0.1,
  GUASS_POW: 1,
  SCALE_GAUSS: false,
  SCALE_RADIUS: false,
  RADIUS_POW: 1,
  PROPEGATE_W: false,
  INITIAL_W_POWER: 4,
  DRAW_KDTREE: false,
  TONE_MASK: true,
  SEARCHRAD2: 1.9851800000000002,

  GEN_CURVE: new cconst.EditableCurve("GEN_CURVE", {
    is_new_curve: true,
    setting_id: "bn9_gui2_SPH_Gen CurveGEN_CURVE",
    generators: [
      {
        type: 1,
        points: [
          { 0: 0, 1: 0, eid: 77, flag: 0, deg: 3, tangent: 1 },
          { 0: 1, 1: 1, eid: 2, flag: 0, deg: 3, tangent: 1 },
          { 0: 1, 1: 1, eid: 3, flag: 0, deg: 3, tangent: 1 },
        ],
        deg: 6,
        eidgen: { _cur: 83 },
      },
      { type: 2, equation: "x" },
      { type: 4, height: 1, offset: 1, deviation: 0.3 },
    ],
    version: 0.5,
    active_generator: 0,
  }),

  SPH_CURVE: {
    is_new_curve: true,
    setting_id: "bn9_gui2_SPH_SPH CurveSPH_CURVE",
    generators: [
      {
        type: 1,
        points: [
          { 0: 0, 1: 0, eid: 1, flag: 0, deg: 3, tangent: 1 },
          { 0: 0.19999999999999998, 1: 0.02499999999999991, eid: 6, flag: 0, deg: 3, tangent: 1 },
          { 0: 0.625, 1: 0.03125000000000022, eid: 4, flag: 0, deg: 3, tangent: 1 },
          { 0: 0.7875, 1: 0.043749999999999845, eid: 3, flag: 0, deg: 3, tangent: 1 },
          { 0: 0.8125000000000001, 1: 0.9500000000000001, eid: 5, flag: 1, deg: 3, tangent: 1 },
          { 0: 0.8250000000000001, 1: 0.9312500000000001, eid: 7, flag: 1, deg: 3, tangent: 1 },
          { 0: 1, 1: 1, eid: 2, flag: 0, deg: 3, tangent: 1 },
        ],
        deg: 6,
        eidgen: { _cur: 8 },
      },
      { type: 2, equation: "x" },
      { type: 4, height: 1, offset: 1, deviation: 0.22460000000000002 },
    ],
    version: 0.5,
    active_generator: 0,
  },
};
/*
  let defaults = {};
  
  for (let k in defaults) {
    if (k in config) {
      config[k] = defaults[k];
    }
  }
  //*/

const gen_curve = function (s: number, ctx?: iface.MaskConfig): number {
  ctx = ctx === undefined ? (config as unknown as iface.MaskConfig) : ctx;

  /*
    s = (1.0-s)*(1.0-s);
    return Math.exp(-(s*ctx.PARAM4)/(2.0*ctx.PARAM5*ctx.PARAM5));
    //*/

  return ctx.GEN_CURVE.evaluate(s);
};
const gen_curve_inv = function (s: number, ctx?: iface.MaskConfig): number {
  ctx = ctx === undefined ? (config as unknown as iface.MaskConfig) : ctx;
  /*
    let f;
    
    if (s == 0.0) return 0.0;
    
    f = 2.0*Math.log(1 / s) / ctx.PARAM4;
    f = f != 0.0 ? Math.sqrt(f) : 0.0;
    f *= Math.abs(ctx.PARAM5);

    return 1.0-f//Math.sqrt(f);
    
    //*/
  return ctx.GEN_CURVE.inverse(s);
};
/*mask toning function
    
    Used either prior to solving or after, see TONE_IN_SOLVER.
    If after, any transformation applied prior to solve is first reversed 
    and then toning is applied (see PrePower, it applies pow(generation, PrePower) prior to solve,
    *but only if if TONE_IN_SOLVER is off*
   */
const tonefunc = function (gen: number, ctx?: iface.MaskConfig): number {
  ctx = ctx === undefined ? (config as unknown as iface.MaskConfig) : ctx;
  return ctx.TONE_CURVE.evaluate(gen);
  /*
    gen = 1.0 - gen;
    let t = 0.75;
    gen = Math.pow(gen, 1.5)*(1.0-t) + Math.pow(gen, ctx.EXPONENT)*t;
    gen = 1.0 - gen;
    //*/

  const off = ctx!.GENSTART;
  //off = ctx.EXPONENT;
  gen = Math.max((gen - off) / (1.0 - off), 0.0);

  const exp2 = 3.25; //ctx.EXP2
  const exp1 = 0.7; //ctx.EXP2; //0.7;  //ctx.EXPONENT

  gen = 1.0 - Math.exp(-gen * exp2);
  gen = gen != 0.0 ? Math.pow(gen, exp1) : 0.0;

  return gen;
};
const tonefunc_inv = function (f: number, ctx?: iface.MaskConfig): number {
  ctx = ctx === undefined ? (config as unknown as iface.MaskConfig) : ctx;
  return ctx.TONE_CURVE.inverse(f);
};
const gridoffs: number[][] = [
  [0, 0],
  [-1, -1],
  [-1, 0],
  [-1, 1],

  [0, 1],
  [1, 1],
  [1, 0],

  [1, -1],
  [0, -1],
];
const calcradius = function (totpoint: number, cconst_ctx: iface.MaskConfig): number {
  return cconst_ctx.RMUL / Math.sqrt(totpoint) / Math.sqrt(2.0);
  //return cconst.RMUL * 1.0 / Math.sqrt(totpoint * 2.0 * Math.sqrt(3.0));
};
const SPHGenerator = class SPHGenerator extends iface.MaskGenerator {
  totpoint: number;
  tick: number;
  totbase: number;
  maxgen: number;

  constructor(appstate?: AppState, dilute_small_mask?: boolean) {
    super(appstate, dilute_small_mask);

    this.skip_point_draw = true;
  }

  static build_ui(gui: UI): void {
    const panel2 = gui.panel("SPH");

    panel2.slider("SPH_SPEED", "SPH Speed", 1.0, 0.001, 15.0, 0.0001, false, false);
    panel2.slider("SEARCHRAD", "Filter Width", 4.0, 0.001, 20.0, 0.0001, false, false);
    panel2.slider("SEARCHRAD2", "Filter2 Width", 2.25, 0.0, 10.0, 0.00001, false, false);
    panel2.slider("RMUL", "rmul", 0.75, 0.0, 10.0, 0.00001, false, false);

    let panel3 = panel2.panel("Additional Settings");
    panel3.slider("START_THRESHOLD", "StartThresh", 0.0, 0.0, 1.0, 0.00001, false, false);
    panel3.slider("GENSTART", "Genstart", 0.0, 0.0, 1.0, 0.00001, false, false);
    panel3.slider("DV_DAMPING", "Damping", 0.0, 0.0, 1.0, 0.00001, false, false);

    panel3.slider("PARAM", "Param1", 1.0, 0.0, 10.0, 0.00001, false, false);
    panel3.slider("PARAM2", "Param2", 2.0, 0.0, 10.0, 0.00001, false, false);
    panel3.slider("PARAM3", "Param3", 0.1, 0.0, 10.0, 0.00001, false, false);
    panel3.slider("PARAM4", "Param4", 4, 0.0, 10.0, 0.00001, false, false);
    panel3.slider("PARAM5", "Param5", 0.72, 0.0, 10.0, 0.00001, false, false);

    panel3.slider("PREPOWER", "PrePower", 0.72, 0.0, 10.0, 0.00001, false, false);

    panel3.check("PROGRESSIVE", "Progressive Mode");
    panel3.check("PROPEGATE_W", "propegate_w");
    /*
    START_THRESHOLD
    GENSTART
    DV_DAMPING
    PROGRESSIVE
    PROPEGATE_W
    INITIAL_W_POWER
    SEARCHRAD2
    SEARCHRAD : 5.0,
      //*/

    panel3 = panel2.panel("Gen Curve");
    window.GEN_CURVE = panel3.curve("GEN_CURVE", "Gen Curve", cconst.DefaultCurves.GEN_CURVE).curve;
    panel3 = panel2.panel("SPH Curve");
    window.SPH_CURVE = panel3.curve("SPH_CURVE", "SPH Curve").curve;

    panel2.close();

    const list: Record<string, string> = {};

    for (const k in sph_presets.presets) {
      list[k] = k;
    }

    //sph_presets
    let sph_val = "Simple";
    panel2.listenum(
      undefined,
      "preset",
      list as unknown as Record<string, number>,
      sph_val,
      function (val: string) {
        sph_val = val;
      }
    );

    panel2.button("load_preset", "Load SPH Preset", function () {
      SPH_CURVE.loadJSON((sph_presets.presets as Record<string, unknown>)[sph_val]);
      SPH_CURVE.update();
      SPH_CURVE.widget.draw();
      SPH_CURVE.widget.save();
    });
  }

  done(): boolean {
    return false;
  }

  reset(dimen: number, appstate: AppState, mask_image: ImageData): void {
    super.reset(dimen, appstate, mask_image);

    this.dimen = dimen;
    this.totpoint = 0;
    this.tick = 0;
    this.r = 0;
    this.totbase = 0.0;
    this.maxgen = 1.0;

    this.throw(this.config);
  }

  max_level(): number {
    return this.maxgen;
  }

  current_level(): number {
    return this.maxgen;
  }

  throw2(): void {
    const totpoint = this.dimen * this.dimen * 0.95;
    const ps = this.points;
    const genstart = totpoint * GENSTART;
    const maxgen = totpoint + genstart;
    const dimen2 = Math.floor(Math.sqrt(totpoint));

    for (let i = 0; i < totpoint; i++) {
      const i2 = i;
      const ix = i2 % dimen2;
      const iy = ~~(i2 / dimen2);
      const x = ix / dimen2;
      const y = iy / dimen2;
      const pi = ps.length;
      for (let j = 0; j < PTOT; j++) {
        ps.push(0.0);
      }

      let gen = i / totpoint;
      gen = Math.fract(gen * Math.PI * 363.324 + y * 23.24);

      if (TONE_IN_SOLVER) {
        gen = tonefunc(gen, cconst as unknown as iface.MaskConfig);
      } else {
        //gen = Math.sqrt(gen);
        gen = Math.pow(gen, PREPOWER);
      }

      const r = 0.8 / Math.sqrt(2 + gen * totpoint);

      ps[pi] = x;
      ps[pi + 1] = y;
      ps[pi + PGEN] = gen;
      ps[pi + POGEN] = gen;
      ps[pi + PR] = r;
    }
  }

  throw(ctx: iface.MaskConfig): void {
    const totpoint = this.dimen * this.dimen * 0.87;
    const ps = this.points;
    const genstart = totpoint * ctx.GENSTART;
    const maxgen = totpoint + genstart;
    const dimen2 = Math.floor(Math.sqrt(totpoint));
    this.r = undefined as unknown as number;
    const mw = this.mask_img.width;
    const mh = this.mask_img.height;

    for (let i = 0; i < totpoint; i++) {
      //let i2 = ~~(Math.random()*totpoint*0.9999);
      //let ix = i2 % dimen2, iy = ~~(i2 / dimen2);
      const x = util.random();
      const y = util.random();
      //let x = ix/dimen2, y = iy/dimen2;
      //x += (util.random()-0.5)/dimen2/3.0;
      //y += (util.random()-0.5)/dimen2/3.0;

      const pi = ps.length;
      for (let j = 0; j < PTOT; j++) {
        ps.push(0.0);
      }

      const ff = genstart / totpoint;

      let gen = i / totpoint;
      gen = ctx.GENSTART + gen * (1.0 - ctx.GENSTART);
      //gen = ff + gen*(1.0 - ff);

      if (ctx.TONE_IN_SOLVER) {
        gen = ctx.tonefunc(gen, ctx);
      } else {
        //gen = Math.sqrt(gen);
        gen = Math.pow(gen, ctx.PREPOWER);
      }

      const r = 0.8 / Math.sqrt(genstart + gen * totpoint);

      ps[pi] = x;
      ps[pi + 1] = y;
      ps[pi + PGEN] = gen;
      ps[pi + POGEN] = gen;
      ps[pi + PR] = r;

      ps[pi + PIX] = ~~(ps[pi] * mw);
      ps[pi + PIY] = ~~(ps[pi + 1] * mh);

      this.r = this.r === undefined ? r : Math.min(r, this.r);
    }

    //XXX get rid of prior radius calculations
    this.r = 1.0 / Math.sqrt(this.points.length / PTOT);
  }
  /*
    makeKDTree() {
      let kd = this.kdtree = new kdtree.KDTree([-2.5, -2.5, -2.5], [2.5, 2.5, 2.5]);
      
      let ps = this.points;
      let co = [0, 0, 0];
      let visit = {};
      let totpoint = 0;

      while (totpoint < ps.length/PTOT) {
        let pi = ~~(Math.random()*0.999999*ps.length/PTOT);
        if (pi in visit) {
          continue;
        }
        
        visit[pi] = 1;
        pi *= PTOT;
        totpoint++;
        
      //for (let pi=0; pi<ps.length; pi += PTOT) {
        kd.insert(ps[pi], ps[pi+1], pi);
      }
      
      return kd;
    }//*/

  updateVelocity(ctx: iface.MaskConfig): void {
    ctx = ctx === undefined ? (cconst as unknown as iface.MaskConfig) : ctx;

    const ps = this.points;

    for (let pi = 0; pi < ps.length; pi += PTOT) {
      const fac = ctx.DV_DAMPING;
      let dx;
      let dy;
      let mindv = 1e17;

      //calc derivatives.  complicated by toroidal domain
      for (const off of gridoffs) {
        const dx2 = ps[pi] - (ps[pi + POLDX] + off[0]);
        const dy2 = ps[pi + 1] - (ps[pi + POLDY] + off[1]);
        const dis = dx2 * dx2 + dy2 * dy2;

        if (dis < mindv) {
          mindv = dis;
          ps[pi + PDX] = dx2;
          ps[pi + PDY] = dy2;
        }
      }

      ps[pi + PDX] *= fac;
      ps[pi + PDY] *= fac;

      ps[pi + POLDX] = ps[pi];
      ps[pi + POLDY] = ps[pi + 1];
    }
  }

  applyVelocity(ctx: iface.MaskConfig): void {
    ctx = ctx === undefined ? (cconst as unknown as iface.MaskConfig) : ctx;

    const ps = this.points;

    for (let pi = 0; pi < ps.length; pi += PTOT) {
      ps[pi] += ps[pi + PDX];
      ps[pi + 1] += ps[pi + PDY];

      ps[pi] = Math.fract(ps[pi]);
      ps[pi + 1] = Math.fract(ps[pi + 1]);
    }
  }

  calcRadii(ctx: iface.MaskConfig): void {
    if (!USE_MASK) {
      return;
    }

    const tree = this.regen_spatial();
    const totpoint = this.points.length / PTOT;
    const ps = this.points;

    this.r = 0.5 / Math.sqrt(totpoint);
    let sumdis = 0.0;
    let sumtot = 0.0;
    let searchr: number;
    let x1: number;
    let y1: number;
    let gen1: number;
    let pi: number;

    const callback = (pi2: number) => {
      if (pi2 == pi) {
        return;
      }
      const x2 = ps[pi2];
      const y2 = ps[pi2 + 1];
      const gen2 = ps[pi2 + PGEN];
      const dx = x2 - x1;
      const dy = y2 - y1;
      let dis = dx * dx + dy * dy;

      if (dis == 0.0 || dis > searchr * searchr) {
        return;
      }

      if (gen2 > gen1) {
        return;
      }

      dis = Math.sqrt(dis);

      let w = 1.0 - dis / searchr;

      w = w * w * w;
      const w2 = (1.0 + gen1) / (1.0 + gen2);
      w *= w2 * w2;

      sumdis += dis * w;
      sumtot += w;
    };

    let minr: number | undefined = undefined;

    for (pi = 0; pi < ps.length; pi += PTOT) {
      (x1 = ps[pi]), (y1 = ps[pi + 1]), (gen1 = ps[pi + PGEN]);
      let r = 6.0 / Math.sqrt((ps.length / PTOT) * 0.075 + (gen1 * ps.length) / PTOT);

      searchr = r;
      sumdis = sumtot = 0.0;
      tree.forEachPoint(x1, y1, searchr, callback);

      if (sumtot != 0.0) {
        r = (sumdis / sumtot) * 0.5; //why do I have to multiply by 1/2?
        ps[pi + PR] = r;

        minr = minr === undefined ? r : Math.min(minr, r);
      } else {
        //estimate
        r = 1.5 / Math.sqrt((ps.length / PTOT) * 0.075 + (gen1 * ps.length) / PTOT);
        ps[pi + PR] = r;
      }

      /*
        r = 0.9 / Math.sqrt(ps.length/PTOT);
        ps[pi+PR] = r;
        //*/
    }

    this.r = minr!;
  }

  jitter(): void {
    const ps = this.points;
    const size = this.r;

    for (let i = 0; i < ps.length; i += PTOT) {
      ps[i] += (util.random() - 0.5) * 2.0 * size;
      ps[i + 1] += (util.random() - 0.5) * 2.0 * size;

      ps[i] = Math.min(Math.max(ps[i], 0.0), 1.0);
      ps[i + 1] = Math.min(Math.max(ps[i + 1], 0.0), 1.0);
    }
  }

  updateFinalGen(ctx: iface.MaskConfig): void {
    const ps = this.points;

    for (let pi = 0; pi < ps.length; pi += PTOT) {
      let gen = ps[pi + PGEN];

      gen = gen_curve_inv(gen, ctx);
      gen = gen != 0.0 ? Math.pow(gen, 1.0 / ctx.PREPOWER) : 0.0;
      gen = (gen - ctx.GENSTART) / (1.0 - ctx.GENSTART);

      ps[pi + PGEN] = gen;
    }
  }

  step(custom_steps?: number, noreport?: boolean): void {
    const ctx = this.config;

    this.applyVelocity(ctx);
    this.smooth_intern(ctx);
    this.updateFinalGen(ctx);
    this.updateVelocity(ctx);

    this.raster();
    this.tick++;
  }

  smooth_intern(ctx: iface.MaskConfig): void {
    ctx = ctx === undefined ? (cconst as unknown as iface.MaskConfig) : ctx;

    window.minfac = window.maxfac = undefined as unknown as number;

    let param = 0.5 + this.tick * 0.125;

    param = Math.min(param, ctx.PARAM);
    window.param = param;

    const thresh = ctx.START_THRESHOLD;
    const ps = this.points;
    const searchfac = ctx.SEARCHRAD;
    const speed = ctx.SPH_SPEED;

    let sumdx: number;
    let sumdy: number;
    let sumw: number;
    let sumtot: number;
    let x1: number;
    let y1: number;
    let r1: number;
    let pi1: number;
    let gen1: number;
    let searchr: number;
    let off: number[];
    let testgen: number;

    const tree = this.regen_spatial();

    this.totbase = 0;

    for (let pi = 0; pi < ps.length; pi += PTOT) {
      const gen = ps[pi + POGEN];

      ps[pi + PGEN] = gen_curve(gen, ctx);

      if (ps[pi + PGEN] < thresh) {
        this.totbase++;
      }

      ps[pi + POLDX] = ps[pi];
      ps[pi + POLDY] = ps[pi + 1];
    }

    const progressive = ctx.PROGRESSIVE;
    const callback = (pi2: number) => {
      if (pi2 == pi1) {
        return;
      }

      const x2 = ps[pi2 + POLDX];
      const y2 = ps[pi2 + POLDY];
      const r2 = ps[pi2 + PR];
      const gen2 = ps[pi2 + PGEN];
      const searchr2 = searchr;
      const dx = x1 + off[0] - x2;
      const dy = y1 + off[1] - y2;
      let dis = dx * dx + dy * dy;

      if (testgen) {
        //  searchr2 = this.r * ((a+dd) / (b+dd) * ctx.PARAM + ctx.PARAM2);
      }

      if (dis == 0.0) {
        // || dis > searchr2*searchr2) {
        return;
      }

      dis = Math.sqrt(dis);
      let w = 1.0 - dis / searchr2;

      if (!progressive) {
        w = SPH_CURVE.evaluate(w);

        sumdx += dx * w;
        sumdy += dy * w;
        sumw += w;
        sumtot += 1.0;

        return;
      } else {
        w = SPH_CURVE.evaluate(w);

        const g2 = gen2 + 0.1;
        const g1 = gen1 + 0.1;

        w *= 1 / (0.00001 + Math.abs(g2 - g1));

        sumdx += dx * w;
        sumdy += dy * w;
        sumw += w;
        sumtot += 1.0;

        return;
      }

      //*
      if (gen2 > gen1 && (testgen || ctx.PROG_BASE_LAYER)) {
        //ctx.PROG_BASE_LAYER && !testgen && gen2 > gen1) {
        //w *= 0.7;

        if (!testgen) {
          const f = (gen1 - gen2) / thresh;
          w *= f;
          //w *= 0.0;
          //dis *= 0.0;
          return;
        }
        //return;
      } else if (!ctx.PROG_BASE_LAYER && !testgen && gen2 > thresh) {
        w *= 0.0;
        dis *= 0.0;
        //return;
      } //*/

      if (/*ctx.PROG_BASE_LAYER ||*/ testgen) {
        //so, this little equation actually *works*.  bleh!
        let a = gen1;
        let b = gen2;
        a = Math.pow(a, ctx.PARAM4);
        b = Math.pow(b, ctx.PARAM4);

        a = ctx.SPH_CURVE.evaluate(a);
        b = ctx.SPH_CURVE.evaluate(b);

        //let a = r2, b = r1;

        //this one works too.  why?
        //let dd = (1.0-b)*ctx.PARAM3 + 0.01;

        const dd = ctx.PARAM3 * this.r + 0.000001;
        //let r = 0.8 / Math.sqrt(this.points.length/PTOT+1);

        let fac = (b + dd) / (a + dd);
        //dd = ctx.PARAM3
        //w = Math.pow(w, ctx.PARAM2 + param*fac);

        fac = param * fac + ctx.PARAM2 + 0.0001;
        if (isNaN(fac)) {
          throw new Error("NaN");
        }

        if (isNaN(w)) {
          console.log(dis, searchr);
          throw new Error("NaN");
        }

        const w0 = w;
        //w = Math.exp(-(1.0-w)/(fac*fac));
        //w = Math.exp(-dis*ctx.PARAM*(b+dd)/(a+dd));

        //w = 1.0 / (1.0 + dis*ctx.PARAM); //*(a+dd)/(b+dd));

        //w = Math.pow(w, fac);

        //w = dis/searchr;

        fac = (b + dd) / (a + dd);

        const max = 1.0 / (ctx.PARAM3 * this.r);
        //fac = Math.pow(fac/max, 2.0)*max;

        //fac = ctx.SPH_CURVE.evaluate(fac/max)*max;
        //w = w * (1.0 + ctx.SPH_CURVE.evaluate(fac/max));

        minfac = minfac === undefined ? fac : Math.min(minfac, fac);
        maxfac = maxfac === undefined ? fac : Math.max(maxfac, fac);

        fac = fac * param + ctx.PARAM2;
        //w = Math.pow(w, fac);
        //w = ctx.SPH_CURVE.evaluate(w);
        w = Math.exp(w * fac) / Math.exp(fac);

        if (isNaN(w)) {
          console.log(w0, dis, fac);
          throw new Error("NaN");
        }
        //w *= w*w*w;
      } else {
        //w = ctx.SPH_CURVE.evaluate(w);
        w = Math.pow(w, 5.0);
      }

      if (isNaN(w)) {
        w = 0.0;
      }
      //if (isNaN(w)) {
      //  throw new Error("NaN");
      //}

      sumdx += dx * w;
      sumdy += dy * w;
      sumw += w;
      sumtot += 1.0;
    };

    let maxr = undefined;
    let threshtot = 0.0;

    for (let pi1 = 0; pi1 < ps.length; pi1 += PTOT) {
      const r1 = ps[pi1 + PR];
      const gen1 = ps[pi1 + PGEN];

      threshtot += +(gen1 < thresh);

      maxr = maxr === undefined ? r1 : Math.max(r1, maxr);
    }

    const startr = 0.8 / Math.sqrt(threshtot + 1);
    const do_startlvl = Math.random() < 0.2;
    const mw = this.mask_img.width;
    const mh = this.mask_img.height;

    //for (let _i=0; _i<ps.length; _i += PTOT) {
    //  pi1 = Math.floor(Math.random()*0.99999*ps.length/PTOT)*PTOT;

    for (pi1 = 0; pi1 < ps.length; pi1 += PTOT) {
      (x1 = ps[pi1 + POLDX]), (y1 = ps[pi1 + POLDY]), (r1 = ps[pi1 + PR]), (gen1 = ps[pi1 + PGEN]);

      let searchr3;

      //make sure initial "layer" is uniform
      if (gen1 < thresh) {
        if (!do_startlvl) {
          //continue;
        }

        //r1 = startr;
        //searchr = startr*ctx.SEARCHRAD2;

        searchr = r1 * ctx.SEARCHRAD2;
        searchr3 = searchr;

        testgen = 0;
      } else {
        const fac = threshtot / (ps.length / PTOT);

        let gen = (gen1 * ps.length) / PTOT;
        gen += threshtot;

        r1 = 0.9 / Math.sqrt(ps.length / PTOT);

        testgen = 1;
        searchr = r1 * searchfac;

        /*
          searchr3 = 0.9 / Math.sqrt(gen) * ctx.SEARCHRAD2;
          searchr3 = searchr3 != 0.0 ? Math.pow(searchr3, 2.0) : 0.0;
          searchr3 = Math.max(searchr3, searchr);
          //*/

        searchr3 = searchr;
      }

      //searchr = this.r * searchfac * 2.5;
      sumdx = sumdy = sumw = sumtot = 0.0;

      //off = gridoffs[0];
      for (off of gridoffs) {
        tree.forEachPoint(x1 + off[0], y1 + off[1], searchr3, callback);
      }
      if (sumw == 0.0) {
        continue;
      }

      sumdx /= sumw;
      sumdy /= sumw;

      let fac = speed * 0.1; //*Math.pow(0.01 + gen1, 2.0);

      if (gen1 < thresh) {
        fac *= ctx.PROG_BASE_LAYER ? 0.05 : 0.45;
      }

      ps[pi1] += sumdx * fac;
      ps[pi1 + 1] += sumdy * fac;

      ps[pi1] = Math.fract(ps[pi1]);
      ps[pi1 + 1] = Math.fract(ps[pi1 + 1]);

      ps[pi1 + PIX] = ~~(ps[pi1] * mw);
      ps[pi1 + PIY] = ~~(ps[pi1 + 1] * mh);

      //ps[pi1] = Math.min(Math.max(ps[pi1], 0.0), 1.0);
      //ps[pi1+1] = Math.min(Math.max(ps[pi1+1], 0.0), 1.0);
    }
  }

  draw(g: CanvasRenderingContext2D): void {
    const d = 0.5;
    //g.clearRect(-d, -d, 1+d*2, 1+d*2);

    const ps = this.points;
    const r = this.r * DRAW_RMUL;

    for (const off of gridoffs) {
      if ((off[0] != 0.0 || off[1] != 0.0) && !DRAW_TILED) {
        continue;
      }

      for (let pi = 0; pi < ps.length; pi += PTOT) {
        let x = ps[pi] + off[0];
        let y = ps[pi + 1] + off[1];
        const gen = ps[pi + POGEN];

        if (DRAW_OFFS) {
          x += ps[pi + POFFX];
          y += ps[pi + POFFY];
        }

        if (gen > DRAW_RESTRICT_LEVEL) {
          continue;
        }

        g.beginPath();
        g.moveTo(x, y);
        g.arc(x, y, r * 0.5, -Math.PI, Math.PI);

        let f1 = gen;
        let f2 = gen * 0.5;

        f1 = ~~(f1 * 255);
        f2 = ~~(f2 * 255);
        g.fillStyle = "rgb(" + f1 + "," + f2 + ",0, 1.0)";
        g.fill();
      }
    }
  }

  toggle_timer_loop(appstate: AppState, simple_mode?: boolean): void {
    if (appstate.timer != undefined) {
      window.clearInterval(appstate.timer);
      appstate.timer = undefined;
      return;
    }

    let start = util.time_ms();
    const first = util.time_ms();
    const lasttot = 0;
    const totsame = 0;

    appstate.timer = window.setInterval(() => {
      if (util.time_ms() - start < 80) {
        return;
      }

      this.config.update();

      const start2 = util.time_ms();
      let report1 = 0;

      while (util.time_ms() - start2 < 150) {
        appstate.step(undefined, report1++ > 0);
      }

      redraw_all();

      const t = util.time_ms() - first;
      const tstr = (t / 1000.0).toFixed(1);

      this.report("time elapsed: ", tstr + "s");
      start = util.time_ms();
    }, 100);
  }
};
// Several functions default a `ctx` arg to the module namespace object; the
// original AMD `exports` served that role, so reconstruct it explicitly.
const exports = { config, gen_curve, gen_curve_inv, tonefunc, tonefunc_inv, SPHGenerator };

iface.MaskGenerator.register(config, SPHGenerator, "SPH", 7);

export { config, gen_curve, gen_curve_inv, tonefunc, tonefunc_inv, SPHGenerator };
